import {
  compareAsc,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  parse,
} from "date-fns";
import { Timeframe, VACANCY_START_AVAILABILITY } from "../../models/vacancy";
import { APITimeframe } from "../../store/vacancy/types";

const formatDate = (date: Date | null): string => {
  return date ? format(date, "dd MMM yyyy") : "";
};

export const transformTimeframesForAPI = (
  timeframes: Timeframe[],
  timelineType: VACANCY_START_AVAILABILITY
) => {
  return timeframes.map((tf, index) => {
    const [start, end] = tf.date || [null, null];
    const formattedStart = formatDate(start);
    const formattedEnd = formatDate(end ?? start);

    const isSingleDate = start && end ? isSameDay(start, end) : true;

    const includeDateRange = !isSingleDate;

    // Handle 'NOW' for the first timeframe
    if (timelineType === VACANCY_START_AVAILABILITY.NOW && index === 0) {
      // Return only end_date and time range; omit start_date/start_time/date_range
      return {
        end_date: formattedEnd,
        end_time: tf.end_time,
      };
    }

    // Default case: include all fields
    const apiTimeframe: APITimeframe = {
      start_date: formattedStart,
      end_date: formattedEnd,
      start_time: tf.start_time,
      end_time: tf.end_time,
    };

    if (includeDateRange) {
      apiTimeframe.date_range = `${formattedStart} - ${formattedEnd}`;
    }

    return apiTimeframe;
  });
};

const parseDate = (dateStr: string): Date => {
  return parse(dateStr, "dd MMM yyyy", new Date());
};

export const transformAPIToFormTimeframes = (
  isEdit: boolean,
  apiTimeframes: APITimeframe[]
): Timeframe[] => {
  return apiTimeframes.map((tf) => {
    const start = parseDate(tf.start_date);
    const end = parseDate(tf.end_date);

    return {
      id: tf?.id || Date.now() + Math.random(), // Ensures uniqueness
      date: [start, end],
      start_time: tf.start_time,
      end_time: tf.end_time,
    };
  });
};

export type DisplayTimeframe = {
  label: string;
  alert: boolean;
};

/**
 * Utility to format a time string (24hr) to 12hr format
 */
function formatTime(time: string): string {
  if (time === "23:59") {
    return "12:00am";
  }

  const [hours, minutes] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes);

  // Format and replace 'AM'/'PM' with 'am'/'pm' and remove space before am/pm
  return format(date, "hh:mm a").replace(" AM", "am").replace(" PM", "pm");
}

/**
 * Formats timeframes with labels and alerts.
 * Shows up to 3, prioritizing upcoming or today.
 * Fills with recent past if needed.
 * Appends "+X more" if extra remain. - getDisplayTimeframes
 * Times are displayed as-is from API, but 24-hour check uses UTC comparison.
 */
export function formatTimeframes(
  timeframes: APITimeframe[],
  returnHtml: boolean = false
): DisplayTimeframe[] {
  const now = new Date();
  const displayedCount = 3;

  const parsed = timeframes?.map((tf) => {
    // Parse the date strings
    const startDate = parse(tf.start_date, "dd MMM yyyy", new Date());
    const endDate = parse(tf.end_date, "dd MMM yyyy", new Date());

    // Parse times for display (as-is from API)
    const [startHours, startMinutes] = tf.start_time.split(":").map(Number);
    const [endHours, endMinutes] = tf.end_time.split(":").map(Number);
    
    // Create local datetime for display and general comparisons
    const startDateTime = new Date(startDate);
    startDateTime.setHours(startHours, startMinutes, 0, 0);
    
    const endDateTime = new Date(endDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    // Create UTC datetime ONLY for 24-hour check
    const startDateTimeUTC = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      startHours,
      startMinutes,
      0,
      0
    ));

    const startDay = format(startDateTime, "EEE dd MMM yyyy");
    const endDay = format(endDateTime, "EEE dd MMM yyyy");

    // Format times as-is from API
    const startTimeFormatted = formatTime(tf.start_time);
    const endTimeFormatted = formatTime(tf.end_time);

    const isSingleDay = tf.start_date === tf.end_date;
    const isTodayFrame = isToday(startDateTime);
    const isTodayEndDate = isToday(endDateTime);
    
    // Check if today is within the date range (comparing dates only, not time)
    const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const isTodayInRange = !isSingleDay && !isAfter(todayDateOnly, endDateOnly) && !isBefore(todayDateOnly, startDateOnly);
    
    // Check if less than 24 hours until start using UTC comparison
    const nowUTC = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds()
    ));
    const hoursUntilStart = (startDateTimeUTC.getTime() - nowUTC.getTime()) / (1000 * 60 * 60);
    const isLessThan24Hours = hoursUntilStart > 0 && hoursUntilStart < 24;
    
    // Keep tomorrow check for label formatting
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrowFrame = isSameDay(startDateTime, tomorrow) || isLessThan24Hours;
    
    const isFuture = isAfter(startDateTime, now);
    const isPast = isBefore(startDateTime, now) && !isTodayFrame && !isTodayInRange;

    let label: string;
    if (returnHtml) {
      if (isTodayFrame) {
        label = isSingleDay
          ? `<div class="text-alert fw-700">Today, ASAP until ${endTimeFormatted}</div>`
          : `<div class="text-alert fw-700">Today to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}</div>`;
      } else if (isTodayInRange) {
        label = isTodayEndDate
          ? `<div class="text-alert fw-700">Today, ${startTimeFormatted} to ${endTimeFormatted}</div>`
          : `<div class="text-alert fw-700">Today to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}</div>`;
      } else if (isTomorrowFrame) {
        label = isSingleDay
          ? `<div class="${isLessThan24Hours ? "text-alert fw-700" : ""}"><span class="fw-700">Tomorrow,</span> ${startTimeFormatted} to ${endTimeFormatted}</div>`
          : `<div class="${isLessThan24Hours ? "text-alert fw-700" : ""}"><span class="fw-700">Tomorrow to ${endDay}</span>, ${startTimeFormatted} to ${endTimeFormatted}</div>`;
      } else if (!isSingleDay) {
        label = `<span class="fw-700">`;
        label += `${startDay} to ${endDay}`;
        label += `</span>, ${startTimeFormatted} to ${endTimeFormatted}`;
      } else {
        label = `<span class="fw-700">`;
        label += `${startDay}`;
        label += `</span>, ${startTimeFormatted} to ${endTimeFormatted}`;
      }
    } else {
      if (isTodayFrame) {
        label = isSingleDay
          ? `Today, ASAP until ${endTimeFormatted}`
          : `Today to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}`;
      } else if (isTodayInRange) {
        label = isTodayEndDate
          ? `Today, ${startTimeFormatted} to ${endTimeFormatted}`
          : `Today to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}`;
      } else if (isTomorrowFrame) {
        label = isSingleDay
          ? `Tomorrow, ${startTimeFormatted} to ${endTimeFormatted}`
          : `Tomorrow to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}`;
      } else if (!isSingleDay) {
        label = `${startDay} to ${endDay}, ${startTimeFormatted} to ${endTimeFormatted}`;
      } else {
        label = `${startDay}, ${startTimeFormatted} to ${endTimeFormatted}`;
      }
    }

    return {
      label,
      alert: isTodayFrame || isTodayInRange || isLessThan24Hours,
      startDate: startDateTime,
      isFuture,
      isPast,
    };
  });

  // Sort all by startDate ascending
  const sorted = parsed.sort((a, b) => compareAsc(a.startDate, b.startDate));

  const futureOrToday = sorted.filter(
    (tf) => tf.isFuture || isToday(tf.startDate) || (tf.alert && !tf.isPast)
  );
  const past = sorted.filter((tf) => tf.isPast);

  // Take up to `displayedCount` from future/today, and fill with recent past if needed
  const result: typeof parsed = [
    ...futureOrToday,
    ...past.reverse(), // reverse to get most recent past
  ].slice(0, displayedCount);

  const sortedResult = result
    .sort((a, b) => compareAsc(a.startDate, b.startDate)) // Ensure final sort order
    .map(({ label, alert }, index, arr) => ({
      label: index < arr.length - 1 ? `${label};` : label,
      alert,
    }));

  const remainingCount = timeframes.length - sortedResult.length;

  if (remainingCount > 0 && sortedResult.length > 0) {
    sortedResult[sortedResult.length - 1].label += ` (+${remainingCount})`;
  }

  return sortedResult;
}

/**
 * Returns the name of today's day of the week.
 * @returns {string} The name of today's day of the week. EX: "Monday"
 */
export const getTodayName = (): string => {
  const weekdayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return weekdayNames[new Date().getDay()];
};
